import { Injectable, Signal, inject } from '@angular/core';
import { DATADOG_RUM_SDK, DatadogRumSdkLike } from './datadog-rum-sdk.token';
import { datadogRumInitializedSignal } from './datadog-rum-state';
import { DatadogRumContext, DatadogRumUser } from './datadog-rum.types';

/**
 * Vendor-neutral wrapper around the Datadog RUM SDK.
 * Every method is a no-op while RUM is not initialized, so it is safe to call from anywhere.
 */
@Injectable({ providedIn: 'root' })
export class DatadogRumFacade {
  private readonly sdk = inject<DatadogRumSdkLike>(DATADOG_RUM_SDK);

  readonly isInitialized: Signal<boolean> = datadogRumInitializedSignal.asReadonly();

  setUser(user: DatadogRumUser): void {
    if (!this.isInitialized()) return;
    this.sdk.setUser(user as Record<string, unknown>);
  }

  setUserProperty(key: string, value: unknown): void {
    if (!this.isInitialized()) return;
    this.sdk.setUserProperty(key, value);
  }

  clearUser(): void {
    if (!this.isInitialized()) return;
    this.sdk.clearUser();
  }

  addAction(name: string, context?: DatadogRumContext): void {
    if (!this.isInitialized()) return;
    this.sdk.addAction(name, context);
  }

  addError(error: unknown, context?: DatadogRumContext): void {
    if (!this.isInitialized()) return;
    this.sdk.addError(error, context);
  }

  setGlobalContextProperty(key: string, value: unknown): void {
    if (!this.isInitialized()) return;
    this.sdk.setGlobalContextProperty(key, value);
  }

  removeGlobalContextProperty(key: string): void {
    if (!this.isInitialized()) return;
    this.sdk.removeGlobalContextProperty(key);
  }

  startView(name: string): void {
    if (!this.isInitialized()) return;
    this.sdk.startView({ name });
  }
}
